import { useState } from 'react'
import { useCustomerAuth } from '@/lib/auth/auth-context'
import { GREY_300, GREY_400, WARNING, WHITE, errorBlockStyle, primaryButtonStyle } from '@/lib/genesis'

/**
 * Customer-portal change-password modal (ADR-179 P2). Same shape and
 * server-revokes-all-sessions UX contract as the operator's
 * components/ChangePasswordModal.tsx, but wired to useCustomerAuth() /
 * /customer/auth/change-password instead — a deliberately separate
 * component (not a shared, mode-switched one) so the customer surface has
 * no import edge into operator auth code.
 *
 * Three-surface UI alignment pass (three-surface-ui-alignment): restyled
 * onto the shared genesis.ts tokens + glass-card idiom, matching the
 * operator console's equivalent modal.
 */
export function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const { changePassword } = useCustomerAuth()
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const mismatch = confirm.length > 0 && newPassword !== confirm

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (newPassword !== confirm) {
      setError('New password and confirmation do not match.')
      return
    }
    setSubmitting(true)
    try {
      // On success this resolves by logging out (server revoked all
      // sessions); the app transitions to the portal login screen, so
      // there is nothing to close.
      await changePassword(oldPassword, newPassword)
      setOldPassword('')
      setNewPassword('')
      setConfirm('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change password.')
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <form onSubmit={handleSubmit} className="glass-card w-full max-w-sm p-5">
        <h2 className="font-sub mb-2 text-base font-semibold" style={{ color: WHITE }}>Change password</h2>
        <p className="mb-4 text-sm" style={{ color: GREY_400 }}>
          Changing your password signs out your other sessions. You will sign
          in again here with the new password.
        </p>

        <label htmlFor="portal-cp-old" className="mb-1 block text-xs font-medium" style={{ color: GREY_400 }}>
          Current password
        </label>
        <input
          id="portal-cp-old"
          type="password"
          autoComplete="current-password"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          className="glass-input mb-3 w-full px-3 py-2 text-sm"
          style={{ color: WHITE }}
          required
        />

        <label htmlFor="portal-cp-new" className="mb-1 block text-xs font-medium" style={{ color: GREY_400 }}>
          New password
        </label>
        <input
          id="portal-cp-new"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="glass-input mb-3 w-full px-3 py-2 text-sm"
          style={{ color: WHITE }}
          required
        />

        <label htmlFor="portal-cp-confirm" className="mb-1 block text-xs font-medium" style={{ color: GREY_400 }}>
          Confirm new password
        </label>
        <input
          id="portal-cp-confirm"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="glass-input mb-3 w-full px-3 py-2 text-sm"
          style={{ color: WHITE }}
          required
        />

        {mismatch && <p className="mb-3 text-xs" style={{ color: WARNING }}>Passwords do not match.</p>}
        {error && (
          <div className="mb-3 p-2" style={errorBlockStyle}>
            <p className="text-xs" style={{ color: WHITE }}>{error}</p>
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="glass-button flex-1 rounded-lg py-2 text-sm disabled:opacity-50"
            style={{ color: GREY_300 }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              submitting ||
              oldPassword.length === 0 ||
              newPassword.length === 0 ||
              confirm.length === 0 ||
              mismatch
            }
            className="flex-1 rounded-lg py-2 text-sm font-semibold disabled:opacity-50"
            style={primaryButtonStyle}
          >
            {submitting ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </form>
    </div>
  )
}
